import React, { PropsWithChildren, createContext, useContext, useMemo, useState } from 'react';

import { PROMOTIONS_API_VERSION, URLS } from '@/data';
import { Product } from '@/hooks/use-jurisdiction';
import { useUserStore } from '@/hooks/use-user-store';
import { logger } from '@/utils/logging';

export interface BonusEngineAPIClient {
    fetch(endpoint: string, options?: RequestInit): Promise<any>;
    setApiUrl(url: string): void;
    apiUrl: string;
}

const BonusEngineClientContext = createContext<BonusEngineAPIClient | undefined>(undefined);

export const useBonusEngineClient = () => {
    const context = useContext(BonusEngineClientContext);
    if (!context) {
        throw new Error('useBonusEngineClient must be used within a BonusEngineClientProvider');
    }
    return context;
};

export const userPromosKeys = {
    all: ['fetch-bonus-engine'] as const,
    genericPromotionsStatus: (slugs: string[]) =>
        [...userPromosKeys.all, ...slugs, 'non-segmented-promotion-status'] as const,
    segmentedPromotions: (userId: string, activeProduct: Product) =>
        [...userPromosKeys.all, 'segmented-promotions', userId, activeProduct] as const,
    depositMatchPromotions: (activeProduct: Product) =>
        [...userPromosKeys.all, 'deposit-match-promotions', activeProduct] as const,
    promoProgress: (promoId: string, userId: string) =>
        [...userPromosKeys.all, 'promo-progress', promoId, userId] as const,
};

export const BonusEngineClientProvider = ({ children }: PropsWithChildren) => {
    const [apiUrl, setApiUrl] = useState(URLS.BONUS_ENGINE_URL);
    const client: BonusEngineAPIClient = useMemo(() => {
        return {
            apiUrl,
            setApiUrl: url => setApiUrl(url),
            fetch: async (endpoint: string, options?: RequestInit) => {
                const { actions, session, guest } = useUserStore.getState();
                if (actions.sessionHasExpired() || guest) {
                    // if the user session has expired or he's a guest don't fetch anything from bonus engine
                    return null;
                }

                const defaultHeaders = {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${session?.access_token}`,
                    'Promotions-Api-Version': PROMOTIONS_API_VERSION,
                };

                const response = await fetch(`${apiUrl}${endpoint}`, {
                    ...options,
                    headers: {
                        ...defaultHeaders,
                        ...options?.headers,
                    },
                });

                if (!response.ok) {
                    logger.warn('[BonuseEngineAPI]', `HTTP error! status: ${response.status}`);
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                if (response.headers.get('content-length') === '0') {
                    // when we activate the promo, the response is empty so we need to return null
                    // so that response.json() doesn't throw an error
                    return null;
                }

                return response.json();
            },
        };
    }, [apiUrl]);
    return <BonusEngineClientContext.Provider value={client}>{children}</BonusEngineClientContext.Provider>;
};
